"use client";
import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-white/5 py-4 px-4">
        <div className="max-w-screen-xl mx-auto flex items-center justify-between">
          <Link href="/landing-page" className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-display font-bold text-base">LookMax AI</span>
          </Link>
          <Link href="/landing-page" className="text-sm text-white/50 hover:text-white transition-colors">
            ← Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-16">
        <h1 className="text-4xl font-display font-bold mb-2">Terms of Service</h1>
        <p className="text-white/40 text-sm mb-12">Last updated: March 20, 2026</p>

        <div className="space-y-10 text-white/70 leading-relaxed">

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">1. Acceptance of Terms</h2>
            <p>
              By accessing or using LookMax AI's website and services at <strong className="text-white">lookmaxers.com</strong> ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service.
            </p>
            <p className="mt-3">
              We reserve the right to update these Terms at any time. Continued use of the Service after changes constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">2. Description of Service</h2>
            <p>
              LookMax AI provides an AI-powered facial analysis and personalized attractiveness improvement roadmap service. Our platform analyzes uploaded photos to generate scores, insights, and actionable recommendations related to appearance, grooming, fitness, and style.
            </p>
            <p className="mt-3">
              The Service is intended for personal, non-commercial use by individuals aged 18 and older.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">3. Eligibility</h2>
            <p>
              You must be at least 18 years of age to use this Service. By using the Service, you represent and warrant that you are 18 years of age or older and have the legal capacity to enter into these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">4. User Accounts</h2>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
              <li>You are responsible for all activities that occur under your account.</li>
              <li>You must provide accurate and complete information when creating an account.</li>
              <li>You must notify us immediately of any unauthorized use of your account.</li>
              <li>We reserve the right to terminate accounts that violate these Terms.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">5. Subscriptions & Payments</h2>
            <p className="mb-3">
              LookMax AI offers subscription plans that provide access to premium features:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Subscription fees are billed in advance on a monthly or annual basis.</li>
              <li>All payments are processed securely through Stripe.</li>
              <li>You may cancel your subscription at any time from your dashboard. Access continues until the end of the current billing period.</li>
              <li>We reserve the right to change subscription pricing with 30 days' notice.</li>
              <li>Refunds are handled on a case-by-case basis. Contact us at <a href="mailto:lookmaxers.ai@gmail.com" className="text-white underline hover:text-white/80 transition-colors">lookmaxers.ai@gmail.com</a> for refund requests.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">6. Acceptable Use</h2>
            <p className="mb-3">You agree not to:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Upload photos of other individuals without their explicit consent</li>
              <li>Use the Service for any unlawful purpose or in violation of any regulations</li>
              <li>Attempt to reverse engineer, decompile, or disassemble any part of the Service</li>
              <li>Use automated tools, bots, or scrapers to access the Service</li>
              <li>Upload content that is illegal, harmful, threatening, abusive, or offensive</li>
              <li>Attempt to gain unauthorized access to any part of the Service or its systems</li>
              <li>Resell, redistribute, or sublicense access to the Service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">7. Intellectual Property</h2>
            <p>
              The Service, including all content, features, and functionality (including but not limited to text, graphics, logos, AI models, and software), is owned by LookMax AI and is protected by intellectual property laws.
            </p>
            <p className="mt-3">
              You retain ownership of photos you upload. By uploading photos, you grant LookMax AI a limited, non-exclusive license to process and analyze those photos solely for the purpose of providing the Service to you.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">8. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. LOOKMAX AI DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
            </p>
            <p className="mt-3">
              AI-generated analysis and recommendations are for informational and entertainment purposes only. They do not constitute medical, dermatological, or professional advice. Always consult qualified professionals for health-related decisions.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">9. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, LOOKMAX AI SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING FROM THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE 12 MONTHS PRECEDING THE CLAIM.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">10. Privacy</h2>
            <p>
              Your use of the Service is also governed by our{' '}
              <Link href="/privacy-policy" className="text-white underline hover:text-white/80 transition-colors">
                Privacy Policy
              </Link>
              , which is incorporated into these Terms by reference.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">11. Termination</h2>
            <p>
              We reserve the right to suspend or terminate your access to the Service at our sole discretion, without notice, for conduct that we believe violates these Terms or is harmful to other users, us, third parties, or for any other reason.
            </p>
            <p className="mt-3">
              Upon termination, your right to use the Service will immediately cease. Provisions of these Terms that by their nature should survive termination shall survive.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">12. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with applicable laws, without regard to conflict of law principles. Any disputes arising from these Terms or the Service shall be resolved through binding arbitration or in the courts of competent jurisdiction.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">13. Contact Us</h2>
            <p>
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <div className="mt-3 p-4 border border-white/10 rounded-xl bg-white/5">
              <p className="text-white font-medium">LookMax AI</p>
              <p className="mt-1">
                Email:{' '}
                <a href="mailto:lookmaxers.ai@gmail.com" className="text-white underline hover:text-white/80 transition-colors">
                  lookmaxers.ai@gmail.com
                </a>
              </p>
              <p className="mt-1">Website: <span className="text-white">lookmaxers.com</span></p>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-10 px-4 mt-16">
        <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-display font-bold text-base">LookMax AI</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-white/40">
            <Link href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link href="/terms-of-service" className="hover:text-white transition-colors text-white">Terms of Service</Link>
            <a href="mailto:lookmaxers.ai@gmail.com" className="hover:text-white transition-colors">Contact</a>
          </div>
          <p className="text-sm text-white/30">© 2026 LookMax AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
